import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { ChecklistItems } from '../../../api/configurations/checklist/types';
import { useChecklist } from '../../../contexts/settings';
import { MiscellaneousData } from '../../../hooks/types';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { FormFooter, RadioGroup } from '../../ui';
import { AskOrderSelect } from './AskOrderSelect';
import MiscellaneousCard from './MiscellaneousCard';
import { ConfirmMethodTitle } from './style';
import { EmailFieldOptions, FormValues, OrderFields, PreparedData } from './types';

type Props = RouteComponentProps & {
  data: MiscellaneousData;
  projectId: string;
  isError: boolean;
  updateData: (values: MiscellaneousData) => void;
};

const MiscellaneousSettings = ({
  data,
  projectId,
  isError,
  updateData,
  history
}: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const [fetching, setFetching] = useState<boolean>(false);
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const { enableChecklistItem } = useChecklist(projectId);

  const preparedData: PreparedData = useMemo(() => {
    return data.reduce((acc, field) => ({ ...acc, [field.name]: field }), {});
  }, [data]);

  const confirmOptions = [
    {
      label: intl.formatMessage({
        id: 'miscellaneous.settings.email.confirmation.code.title'
      }),
      description: intl.formatMessage({
        id: 'miscellaneous.settings.email.confirmation.code.description'
      }),
      radioValue: 'code'
    },
    {
      label: intl.formatMessage({
        id: 'miscellaneous.settings.email.confirmation.link-only.title'
      }),
      description: intl.formatMessage({
        id: 'miscellaneous.settings.email.confirmation.link-only.description'
      }),
      radioValue: 'link'
    }
  ];

  const onSubmit = async (values: FormValues) => {
    setFetching(true);
    try {
      const finalData = Object.keys(OrderFields).reduce((acc, name) => {
        const fieldInfo = values[name as OrderFields];
        const isFieldConnect = values[`${name as OrderFields}_collect`];
        if (!isFieldConnect) {
          return acc;
        }
        return [
          ...acc,
          {
            ...fieldInfo,
            name,
            map_name: name,
            type: name === OrderFields.phone_number ? 'phone' : name,
            order: 0,
            confirmation_type:
              name === OrderFields.phone_number
                ? 'code'
                : (fieldInfo as EmailFieldOptions).confirmation_type,
            step: values.field_order === name ? 0 : 1
          }
        ];
      }, []);
      await updateData(finalData);
      await enableChecklistItem(ChecklistItems.MISCELLANEOUS_DATA);
      showSuccessNotify();
    } catch (error) {
      showErrorNotify(error);
    } finally {
      setFetching(false);
    }
  };

  const onClose = () => history.push({ pathname: navigationPath(projectId) });

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        email_collect: !!preparedData.email,
        phone_number_collect: !!preparedData.phone_number,
        field_order: !preparedData.email?.step ? OrderFields.email : OrderFields.phone_number,
        email: {
          required: !!preparedData?.email?.required
        },
        phone_number: {
          required: !!preparedData?.phone_number?.required
        }
      }}>
      {({ handleSubmit, values, pristine }) => {
        return (
          <>
            <MiscellaneousCard name={OrderFields.email}>
              <ConfirmMethodTitle>
                {intl.messages['miscellaneous.settings.email.confirmation.title']}
              </ConfirmMethodTitle>
              <Field name="email.confirmation_type" type="radio">
                {(props) => (
                  <RadioGroup
                    name={props.input.name}
                    onChange={props.input.onChange}
                    defaultValue={preparedData?.email?.confirmation_type || 'code'}
                    options={confirmOptions}
                  />
                )}
              </Field>
            </MiscellaneousCard>
            <MiscellaneousCard name={OrderFields.phone_number} />
            <AskOrderSelect values={values} />
            <FormFooter
              error={isError}
              onSubmit={handleSubmit}
              fetching={fetching}
              disabled={pristine}
              onCancel={onClose}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(MiscellaneousSettings);
